import {
  COOKS_DOC,
  COOKS_MAP_KEY,
  lastCookOf,
  type CookLogEntry,
  type Recipe,
} from '@awwyeah/shared';
import type * as Y from 'yjs';
import { useYDoc, useYMap } from '../../sync';
import { newId } from './utils/id';

/**
 * Read/write access to the cook log (`COOKS_DOC`) for the recipes module — the "Cooked
 * this" action's whole data layer. Same conventions as `./hooks.ts`: hooks open the doc and
 * hand back both a live snapshot and the `Y.Map` to write to, and the mutators are plain
 * functions taking that map, so they are callable from an event handler without a hook
 * dance and testable without React.
 *
 * Writes go straight to Yjs (ROADMAP §1.3) — logging a cook is a local, instant, offline-
 * safe `map.set`, which is exactly the point: you tap "Cooked this" standing in a kitchen,
 * and whether the phone has signal is not part of the transaction.
 */

/** Opens the shared `cooks` doc for as long as the caller is mounted. */
export function useCooksDoc(): Y.Doc {
  return useYDoc(COOKS_DOC);
}

/** Live view of every cook entry, plus the map to write to. Unsorted — callers that need
 * an order say so (`lastCookOf`, `sortCooksNewestFirst`). */
export function useCooks(doc: Y.Doc): { map: Y.Map<CookLogEntry>; cooks: CookLogEntry[] } {
  const { map, entries } = useYMap<CookLogEntry>(doc, COOKS_MAP_KEY);
  return { map, cooks: entries.map(([, cook]) => cook).filter(Boolean) };
}

/** The most recent cook of one recipe, or `undefined` — the only cook history the detail
 * page reads. No count is exposed here or anywhere downstream (see `cooks.ts` in shared). */
export function useLastCook(doc: Y.Doc, recipeId: string | undefined): CookLogEntry | undefined {
  const { cooks } = useCooks(doc);
  return recipeId ? lastCookOf(cooks, recipeId) : undefined;
}

/**
 * Records that `userId` cooked `recipe`, now. Returns the entry so the caller can offer
 * undo — mis-taps happen, and a fact card that appears in the family timeline because a
 * thumb brushed a button should be as easy to take back as it was to create.
 *
 * `recipeTitle` is snapshotted here rather than resolved at render time; see the schema's
 * docblock for why a cook has to outlive its recipe.
 */
export function logCook(
  map: Y.Map<CookLogEntry>,
  recipe: Pick<Recipe, 'id' | 'title'>,
  userId: string,
  note?: string,
): CookLogEntry {
  const trimmed = note?.trim();
  const entry: CookLogEntry = {
    id: newId(),
    recipeId: recipe.id,
    recipeTitle: recipe.title,
    cookedBy: userId,
    cookedAt: new Date().toISOString(),
    ...(trimmed ? { note: trimmed } : {}),
  };
  map.set(entry.id, entry);
  return entry;
}

/** The undo half of `logCook` — a real delete, not a tombstone. An un-done cook never
 * happened as far as the timeline is concerned. */
export function unlogCook(map: Y.Map<CookLogEntry>, cookId: string): void {
  map.delete(cookId);
}
